import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import {
  Box,
  Link as MuiLink,
  ScreenReaderOnly,
  TableColumn,
} from '@ally/metronome-ui'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import { Cell, Row } from 'react-table'
import { DashboardView } from '../../../CustomizeDashboard/types'
import { AccountDetailsLink, TextBodyOverride } from '../../Shared'
import { formatPercentage, getDashDash, isAccountClosed } from '../../../utils'
import { STALE_DATA_COPY } from '../../../constants'

const StyledWrapper = styled.div`
  ${({ theme: { media } }): string =>
    `
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 0;
    min-width: 100%;

  ${media.up('xl')} {
    display: inline;
  }
`};
`

const StyledMuiLink = styled(MuiLink)`
  ${({ theme: { colors } }): string =>
    `
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;
    vertical-align: middle;
    display: inline-block;

    @media print {
      display: inline;
      color: ${colors['slate-5']};
    }
  `};
`

interface CellProps {
  cell: Cell<Account>
  row: Row<Account>
}

export interface WealthTableData {
  accounts: Account[]
  totalValue: number
  netYield?: number | null
  netReturn?: number | null
}

interface FooterProps {
  rows: Row<Account>[]
}

interface GetColumnProps {
  hasOnlyClosedAccounts: boolean
  isLgDown: boolean
  isWealthPartialOutage: boolean
  view: DashboardView
  hasStaleData: boolean
  isPersonalAdvisorEnabled: boolean
}

export const getColumns = ({
  hasOnlyClosedAccounts,
  isLgDown,
  isWealthPartialOutage,
  view,
  hasStaleData,
  isPersonalAdvisorEnabled,
}: GetColumnProps): TableColumn<Account>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown
  const closedText = (
    <TextBodyOverride tag="span" size="sm" color="error">
      CLOSED
    </TextBodyOverride>
  )

  const headerCopy = isPersonalAdvisorEnabled
    ? 'Personal Advice'
    : 'Wealth Management'

  if (hasOnlyClosedAccounts) {
    const columns = {
      accountNickname: {
        Header: headerCopy,
        accessor: 'nickName',
        alignment: 'left',
        isSortable: true,
        Cell: ({ row }: { row: Row<Account> }): React.ReactNode | string => {
          if (row.original.id === 'wealth-overview') {
            return (
              <StyledWrapper>
                <StyledMuiLink
                  text="Wealth Overview"
                  allytmln="wealth-wealthOverview"
                >
                  <Link to="/ext-wealth/wealth-overview" />
                </StyledMuiLink>
              </StyledWrapper>
            )
          }
          return (
            <>
              <AccountDetailsLink
                account={row.original}
                text={row.original.nickName || row.original.name}
                allytm="wealthAccount"
              />
              <Box display="inline-block" aria-hidden="true">
                {maskAccountNumber(row.original.accountNumber)}
              </Box>
            </>
          )
        },
      },
      accountStatus: {
        Header: <ScreenReaderOnly>Account Status</ScreenReaderOnly>,
        accessor: 'status',
        alignment: isDefaultView ? 'left' : 'right',
        isSortable: true,
        Cell: ({ row }: { row: Row<Account> }): React.ReactNode | string => {
          if (row.original.id === 'wealth-overview') {
            return ''
          }
          return closedText
        },
      },
      empty: {
        Header: '',
        accessor: 'empty',
      },
      empty2: {
        Header: '',
        accessor: 'empty2',
      },
    }
    return isDefaultView
      ? ([columns.accountNickname, columns.accountStatus] as TableColumn<
          Account
        >[])
      : ([
          columns.accountNickname,
          columns.accountStatus,
          columns.empty,
          columns.empty2,
        ] as TableColumn<Account>[])
  }

  const columns = {
    accountNickname: {
      Header: headerCopy,
      accessor: 'nickName',
      alignment: 'left',
      isSortable: true,
      Cell: ({ row }: { row: Row<Account> }): React.ReactNode | string => {
        if (row.original.id === 'wealth-overview') {
          return (
            <StyledWrapper>
              <StyledMuiLink
                text="Wealth Overview"
                allytmln="wealth-wealthOverview"
              >
                <Link to="/ext-wealth/wealth-overview" />
              </StyledMuiLink>
            </StyledWrapper>
          )
        }
        return (
          <>
            <AccountDetailsLink
              account={row.original}
              text={row.original.nickName || row.original.name}
              allytm="wealthAccount"
            />
            <Box display="inline-block" aria-hidden="true">
              {maskAccountNumber(row.original.accountNumber)}
            </Box>
          </>
        )
      },
      Footer: 'Total',
    },
    accountValueTotal: {
      Header: 'Account Value',
      accessor: 'value.total',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        if (row.original.id === 'wealth-overview') {
          return ''
        }
        const isClosed = isAccountClosed(row.original)
        if (!isClosed && value === 0) {
          return (
            <MuiLink text="Add money" allytmln="Wealth Add Money">
              <Link to="/ext-invest-live/transfer" />
            </MuiLink>
          )
        }
        return isClosed ? closedText : asCurrency(value)
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce(
              (sum, row) => (row.values['value.total'] ?? 0) + sum,
              0,
            ),
          [rows],
        )

        return asCurrency(total)
      },
    },
    netReturnPercent: {
      Header: 'Net Return %',
      accessor: 'value.netReturnPercent',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        const isClosed = isAccountClosed(row.original)

        if (isClosed || row.original.id === 'wealth-overview') {
          return ''
        }

        return isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(value)
      },
    },
    yieldPercent: {
      Header: 'Yield %',
      accessor: 'value.yieldPercent',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        const isClosed = isAccountClosed(row.original)

        if (isClosed || row.original.id === 'wealth-overview') {
          return ''
        }

        return isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(value)
      },
    },
    actions: {
      Header: '',
      accessor: 'actions',
      alignment: 'right',
      isSortable: false,
      Cell: (): React.ReactNode | string => '',
    },
  }

  const tileViewCols = [
    columns.accountNickname,
    columns.accountValueTotal,
    columns.netReturnPercent,
    columns.actions,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountNickname,
    columns.accountValueTotal,
    columns.netReturnPercent,
    columns.yieldPercent,
    columns.actions,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
